import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { Ticket } from "@lib/titan2-slingo/dist/game/view/abstract/display/Ticket";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { gsap } from "gsap";
import * as PIXI from "pixi.js";
import { TicketViewEvent } from "../../../controller/event/GameEvent";
import { SlingoStyle } from "@lib/titan2-slingo/dist/game/view/abstract/style/SlingoStyle";
import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { Array2D } from "@lib/titan2/dist/util/collection/Array2D";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { TitanGraphics } from "./TitanGraphics";
import { Graphics, Texture } from "pixi.js";
/**
 * GameTicket.ts
 */
export class GameTicket extends Ticket {
    constructor() {
        super(...arguments);
        this.nextAwardIsRetrigger = false;
    }
    addEventListeners() {
        super.addEventListeners();
        this.unregisterGame(SlingoEvent.SLINGO);
        this.registerGame(SlingoEvent.SLINGO_COMPLETE, this.handleExternalSlingoComplete);
        this.registerGame(TicketViewEvent.SLINGO_TICKET_ANIMATION, this.handleSlingo);
        this.registerGame(GameTicketEvents.TRANSITION_TO_FEATURE_FADE_OUT, this.handleTransitionToFeatureFadeOut);
        this.registerGame(GameTicketEvents.TRANSITION_FROM_FEATURE_FADE_IN, this.handleTransitionFromFeatureFadeIn);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_START, this.showMouseBlockerUnderSymbols);
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.showMouseBlockerAboveSymbols);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_CLEAR, this.showMouseBlockerUnderSymbols);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_END, this.hideMouseBlockers);
    }
    createBackground() {
        const createReelAreaTexture = (width, height, colour, alpha) => {
            const graphics = new Graphics();
            graphics.beginFill(colour, alpha);
            graphics.drawRect(0, 0, width, height);
            return Texture.from(this.renderer.getRenderer().generateTexture(graphics, { resolution: 1 }).baseTexture);
        };
        const landscapeReelAreaTexture = createReelAreaTexture(650, 650, 0x000000, 0.35);
        const portraitReelAreaTexture = createReelAreaTexture(833, 833, 0x000000, 0.35);
        this.mouseBlockerUnderSymbols = TitanGraphics.createSprite(this, "reelArea", this.landscapeProperties.reelAreaProperties, this.portraitProperties?.reelAreaProperties, landscapeReelAreaTexture, portraitReelAreaTexture);
        this.mouseBlockerUnderSymbols.visible = false;
        this.addChild(this.mouseBlockerUnderSymbols);
        this.mouseBlockerAboveSymbols = TitanGraphics.createSprite(this, "reelArea", this.landscapeProperties.reelAreaProperties, this.portraitProperties?.reelAreaProperties, landscapeReelAreaTexture, portraitReelAreaTexture);
        this.mouseBlockerAboveSymbols.visible = false;
        this.addChild(this.mouseBlockerAboveSymbols);
    }
    createSymbols() {
        if (!this.symbolsPanelContainer) {
            this.symbolsPanelContainer = new PIXI.Container();
            this.addChild(this.symbolsPanelContainer);
        }
        this.symbolsByNumber = new Dictionary();
        this.symbolsPanel = TitanPanel.createPanel(this.symbolsPanelContainer, "symbolsPanel", this.landscapeProperties.symbolPanelProperties, this.portraitProperties.symbolPanelProperties);
        let ticket = this.model.getTicket();
        let rows = ticket.getNumberOfRows();
        let columns = ticket.getNumberOfColumns();
        this.symbols = new Array2D(rows, columns);
        this.allSymbols = [];
        for (let row = 0; row < rows; row++) {
            for (let column = 0; column < columns; column++) {
                let symbol = this.createSymbol();
                symbol.setTrackingId(`${SlingoStyle.TICKET_SYMBOL}_${column}_${row}`);
                this.symbolsPanel.addToPanel(symbol, false);
                this.symbols.setValue(row, column, symbol);
                let value = ticket.getCellAt(row, column);
                this.symbolsByNumber.setValue(value.getNumber(), symbol);
                symbol.name = row + "_" + column + "_val_" + value.getNumber();
                this.allSymbols.push(symbol);
            }
        }
        this.symbolsPanel.arrange();
        this.addChild(this.mouseBlockerAboveSymbols);
    }
    destroySymbols() {
        if (this.symbolsPanel) {
            this.symbolsPanel.removeAll();
        }
        if (this.symbolsByNumber) {
            this.symbolsByNumber.clear();
        }
        if (this.symbols) {
            let ticket = this.model.getTicket();
            let rows = ticket.getNumberOfRows();
            let columns = ticket.getNumberOfColumns();
            for (let row = 0; row < rows; row++) {
                for (let column = 0; column < columns; column++) {
                    let symbol = this.symbols.getValue(row, column);
                    gsap.killTweensOf(symbol);
                    symbol.destroy();
                }
            }
        }
        this.symbols = null;
        DestroyUtil.destroy(this.symbolsPanel);
        this.symbolsPanel = null;
    }
    handleTransitionToFeatureFadeOut() {
        gsap.to(this, { alpha: 0, onComplete: () => (this.visible = false) });
    }
    handleTransitionFromFeatureFadeIn() {
        this.visible = true;
        this.alpha = 0;
        gsap.to(this, { alpha: 1 });
    }
    handleSlingo() {
        super.handleSlingo();
        this.showMouseBlockerUnderSymbols();
    }
    handleSlingoComplete() {
        this.dispatchGame(TicketViewEvent.SLINGO_TICKET_ANIMATION_COMPLETE);
    }
    handleExternalSlingoComplete() {
        this.hideMouseBlockers();
    }
    handleResize(event) {
        super.handleResize(event);
        if (this.symbolsPanel) {
            this.symbolsPanel.scale.set(this.uiProperties.symbolPanelProperties.scale.x, this.uiProperties.symbolPanelProperties.scale.y);
            this.symbolsPanel.position.set(this.uiProperties.symbolPanelProperties.position.x, this.uiProperties.symbolPanelProperties.position.y);
        }
    }
    animateSymbols() {
        let symbols = this.symbolsToAnimate[this.symbolAnimationIndex];
        for (let i = 0; i < symbols.length; i++) {
            let point = symbols[i];
            let symbol = this.symbols.getValue(point.x, point.y);
            symbol.fullHouse();
        }
        let delay = this.uiProperties.fullHousePatternDuration / this.symbolsToAnimate.length;
        if (this.symbolAnimationIndex + 1 < this.symbolsToAnimate.length) {
            this.symbolAnimationIndex++;
            this.callAfter(delay, this.animateSymbols);
        }
        else {
            // this.handleFullHouseSymbolsComplete();
        }
    }
    handleFullHouseTimerComplete() {
        //this.dispatchGame(SlingoEvent.FULL_HOUSE_ANIMATION_PHASE_TWO);
    }
    handleFullHouseAnimationPhaseOne() {
        this.symbolsToAnimate = this.uiProperties.fullHousePattern;
        this.symbolAnimationIndex = 0;
        gsap.delayedCall(2, () => {
            this.dispatchGame(SlingoEvent.FULL_HOUSE_ANIMATION_PHASE_TWO);
        });
        this.animateSymbols();
    }
    handleBigWinAnimationPhaseOne() {
        // noop, as this game has no bigWin celebrations
    }
    highlightCell(cell, animate, superJoker) {
        let symbol = this.symbolsByNumber.getValue(cell.getNumber());
        if (superJoker) {
            symbol.highlightSuperWild(animate);
        }
        else {
            symbol.highlight(animate);
        }
    }
    highlightNumberCells(cells) {
        for (const cell of cells) {
            const symbol = this.symbolsByNumber.getValue(cell.getNumber());
            symbol.highlight(true);
        }
    }
    highlightSuperJokerCells(cells) {
        for (const cell of cells) {
            const symbol = this.symbolsByNumber.getValue(cell.getNumber());
            symbol.highlightSuperWild(true);
            symbol.enable();
        }
    }
    highlightJokerCells(cells) {
        for (const cell of cells) {
            const symbol = this.symbolsByNumber.getValue(cell.getNumber());
            symbol.highlightWild(true);
            symbol.enable();
        }
    }
    showMouseBlockerAboveSymbols() {
        this.mouseBlockerAboveSymbols.alpha = 1;
        this.mouseBlockerUnderSymbols.alpha = 1;
        this.mouseBlockerAboveSymbols.visible = true;
        this.mouseBlockerUnderSymbols.visible = false;
        let availableWin = this.model.getAvailableWin();
        let cells = availableWin.getCells();
        this.couldWinSymbols = [];
        for (const cell of cells) {
            const existingSymbol = this.symbolsByNumber.getValue(cell.getNumber());
            const dataClone = existingSymbol.getData();
            const symbolClone = this.factory.createTicketSymbol();
            symbolClone.update(dataClone);
            this.addChild(symbolClone);
            this.couldWinSymbols.push(symbolClone);
            symbolClone.disable();
            symbolClone.showWin();
        }
        this.updateCouldWinSymbolPositionsAndScales();
    }
    updateCouldWinSymbolPositionsAndScales() {
        if (this.couldWinSymbols) {
            this.couldWinSymbols.forEach((symbol) => {
                const existingSymbol = this.symbolsByNumber.getValue(symbol.getData().getNumber());
                const existingWorldPosition = existingSymbol.parent.toGlobal(existingSymbol.position);
                const localPosition = this.toLocal(existingWorldPosition);
                symbol.position.copyFrom(localPosition);
                symbol.scale.copyFrom(existingSymbol.parent.parent.parent.scale);
            });
        }
    }
    showMouseBlockerUnderSymbols(animate = false) {
        if (animate) {
            gsap.to(this.mouseBlockerUnderSymbols, { alpha: 1, duration: 0.3 });
            gsap.to(this.mouseBlockerAboveSymbols, { alpha: 1, duration: 0.3 });
        }
        this.mouseBlockerAboveSymbols.alpha = 1;
        this.mouseBlockerUnderSymbols.alpha = 1;
        this.mouseBlockerAboveSymbols.visible = false;
        this.mouseBlockerUnderSymbols.visible = true;
        this.destroyCouldWinSymbols();
    }
    hideMouseBlockers() {
        gsap.to(this.mouseBlockerUnderSymbols, { alpha: 0, duration: 0.25 });
        gsap.to(this.mouseBlockerAboveSymbols, {
            alpha: 0,
            duration: 0.25,
            onComplete: () => {
                this.mouseBlockerAboveSymbols.visible = false;
                this.mouseBlockerUnderSymbols.visible = false;
            },
        });
        this.destroyCouldWinSymbols();
    }
    destroyCouldWinSymbols() {
        if (this.couldWinSymbols) {
            this.couldWinSymbols.forEach((symbol) => {
                symbol.destroy();
            });
            this.couldWinSymbols.length = 0;
        }
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.updateCouldWinSymbolPositionsAndScales();
    }
}
export class GameTicketEvents {
}
GameTicketEvents.TRANSITION_TO_FEATURE_FADE_OUT = new EventObject("GameTicketEvents.TRANSITION_TO_FEATURE_FADE_OUT");
GameTicketEvents.TRANSITION_FROM_FEATURE_FADE_IN = new EventObject("GameTicketEvents.TRANSITION_FROM_FEATURE_FADE_IN");
//# sourceMappingURL=GameTicket.js.map